import Column from '@amzn/meridian/column';
import { FileDetails } from '@amzn/meridian/file-input';
import { FileDetailsFile } from '@amzn/meridian/file-input/file-details';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { PutClient } from '../clients';
import { useHrpsApi } from '../clients/useHrpsApi';
import { Heading, Link, useTranslation } from '../components/blocks';
import { Button, FileInput, Text } from '../components/blocks/localization';
import { Tile, usePageMessaging } from '../components/composites';
import GeneratePreFilledClaimTemplatePopup from '../components/composites/GeneratePreFilledClaimTemplatePopup';
import { useModal } from '../components/composites/Modal';
import { TemplateSelectionPopup } from '../components/composites/TemplateSelectionPopup';
import { HelpPopover } from '../components/constructed';
import {
    ColumnFormat,
    SuperPaginatedTable,
    SuperPaginatedTableColumn,
} from '../components/constructed/SuperPaginatedTable';
import { textFormat } from '../components/constructed/SuperPaginatedTable.formats';
import { usePdfClaimsFeature } from '../hooks/usePdfClaimsFeature';
import { usePreFilledPromotionalClaimGenerator } from '../hooks/usePreFilledPromotionalClaimGenerator';
import { AttachedDocument, ClaimType, RootState } from '../redux/types';
import { ClaimReceiptMethods, ClaimUploadProgress } from '../redux/types/claim';
import { ClaimProcessingError } from '../redux/types/claimList';
import { ProcessedPdfClaimStatus, ProcessPdfClaimUploadOutput } from '../types';
import { ProcessClaimUploadOutput, ProcessedClaimStatus } from '../types/ProcessClaimUpload';
import { delay } from '../utils/delay';
import { Logger } from '../utils/logger';
import { useSiteMapRouter } from '../utils/SiteMapRouter';
import { sanitizeFilenameISO88591 } from '../utils/textUtils';

const SCS_IMPORT_WAIT_TIME_MS = 1000;

/* eslint max-lines-per-function: ["off"] */
const UploadClaimPage: FC<UploadClaimPageProps> = ({ user, resellerId, countryCode }) => {
    const { t } = useTranslation('uploadClaimPage');
    const errorT = useTranslation('claimStatus')[0];
    const { cancel } = useSiteMapRouter();
    const modal = useModal();
    const { showError } = usePageMessaging();
    const { isPdfClaimEnabled } = usePdfClaimsFeature();
    const { isPreFilledClaimGeneratorEnabled } = usePreFilledPromotionalClaimGenerator();

    const [isPageBusy, setIsPageBusy] = useState(false);
    const [isOverride, setIsOverride] = useState(false);
    const [receiptDates, setReceiptDates] = useState<Date[]>([]);
    const [receiptMethods, setReceiptMethods] = useState<ClaimReceiptMethods[]>([]);
    const [claimTypes, setClaimTypes] = useState<(ClaimType | undefined)[]>([]);
    const [progress, setProgress] = useState<ClaimUploadProgress[]>([]);
    const [files, setFiles] = useState<File[]>([]);
    const [filesListContainsPdf, setFilesListContainsPdf] = useState(false);
    const [processResults, setProcessResults] = useState<ProcessedClaimStatus[] | ProcessedPdfClaimStatus[]>([]);
    const [templates, setTemplates] = useState<AttachedDocument[]>([]);
    const amazonInternalRoles = ['amazonAdmin', 'amazonCreator'];

    // Currently only the TPR claim type is supported for PDF Claims.
    // TODO: Remove this and its usages when other claim types are enabled.
    const areNonTprClaimTypesEnabledForPdfClaims = false;

    const {
        claims: { requestClaimUploadUrl, processClaimUpload, processPdfClaimUpload, getClaimUploadTemplates },
    } = useHrpsApi();

    const loadTemplates = useCallback(async () => {
        const result = await getClaimUploadTemplates();
        const countrySpecificTemplates = result.templates.filter((t) => t.countryCode === countryCode);
        const defaultTemplates = result.templates.filter((t) => t.countryCode === null);
        setTemplates(countrySpecificTemplates.length ? countrySpecificTemplates : defaultTemplates);
    }, [getClaimUploadTemplates, countryCode]);

    useEffect(() => {
        if (templates.length === 0) {
            loadTemplates();
            if (user?.roles.some((role) => amazonInternalRoles.includes(role))) {
                setIsOverride(true);
            } else {
                setIsOverride(false);
            }
        }
    }, [loadTemplates, templates.length, user, amazonInternalRoles]);

    const setFileProgress = (fileName: string, progressStat: number) => {
        const newProgress = progress.filter((f) => f.fileName !== fileName);
        newProgress.push({
            fileName: fileName,
            progress: progressStat,
        });
        setProgress(newProgress);
    };

    const setOrUpdateFiles = async (inputFiles: File[]) => {
        if (!files.length) {
            setFiles(inputFiles);
            setFilesListContainsPdf(inputFiles.some((file) => file.type === 'application/pdf'));
            setReceiptDates(new Array(inputFiles.length).fill(isOverride ? undefined : new Date()));
            setReceiptMethods(
                new Array(inputFiles.length).fill(isOverride ? undefined : ClaimReceiptMethods.CLAIM_TEMPLATE_UPLOAD)
            );
            // TODO: When non-TPR PDF claims are enabled, update to:
            // setClaimTypes(new Array(inputFiles.length).fill(undefined));
            setClaimTypes(updateClaimTypesForNewFiles(inputFiles));
        } else {
            const filteredFiles = inputFiles.filter((file) => !files.some((f) => f.name === file.name));
            const newFiles = [...files, ...filteredFiles];
            const newReceiptDates = [
                ...receiptDates,
                ...new Array(filteredFiles.length).fill(isOverride ? undefined : new Date()),
            ];
            const newReceiptMethods = [
                ...receiptMethods,
                ...new Array(filteredFiles.length).fill(
                    isOverride ? undefined : ClaimReceiptMethods.CLAIM_TEMPLATE_UPLOAD
                ),
            ];
            const newClaimTypes = [
                ...claimTypes,
                // TODO: When non-TPR PDF claims are enabled, update to:
                // ...new Array(filteredFiles.length).fill(undefined),
                ...updateClaimTypesForNewFiles(filteredFiles),
            ];

            setFiles(newFiles);
            setFilesListContainsPdf(newFiles.some((file) => file.type === 'application/pdf'));
            setReceiptDates(newReceiptDates);
            setReceiptMethods(newReceiptMethods);
            setClaimTypes(newClaimTypes);
        }
    };

    // TODO: Remove when non-TPR PDF claims are enabled
    const updateClaimTypesForNewFiles = (newFiles: File[]) => {
        return new Array(newFiles.length).fill(undefined).map((_, index) => {
            const file = newFiles[index];
            const isPDF = file.type === 'application/pdf';

            if (isPDF && !areNonTprClaimTypesEnabledForPdfClaims) {
                return ClaimType.TPR;
            } else {
                return undefined;
            }
        });
    };

    const deleteFile = async (fileIndex: number) => {
        const newFiles = [...files];
        const newReceiptDates = [...receiptDates];
        const newReceiptMethods = [...receiptMethods];
        const newClaimTypes = [...claimTypes];
        newFiles.splice(fileIndex, 1);
        newReceiptDates.splice(fileIndex, 1);
        newReceiptMethods.splice(fileIndex, 1);
        newClaimTypes.splice(fileIndex, 1);
        setFiles(newFiles);
        setFilesListContainsPdf(newFiles.some((file) => file.type === 'application/pdf'));
        setReceiptDates(newReceiptDates);
        setReceiptMethods(newReceiptMethods);
        setClaimTypes(newClaimTypes);
    };

    const updateReceiptDate = async (date: Date, index: number) => {
        const newReceiptDates = [...receiptDates];
        newReceiptDates[index] = date;
        setReceiptDates(newReceiptDates);
    };

    const updateReceiptMethod = async (method: ClaimReceiptMethods, index: number) => {
        const newReceiptMethods = [...receiptMethods];
        newReceiptMethods[index] = method;
        setReceiptMethods(newReceiptMethods);
    };

    const updateClaimType = async (claimType: ClaimType, index: number) => {
        const newClaimTypes = [...claimTypes];
        newClaimTypes[index] = claimType;
        setClaimTypes(newClaimTypes);
    };

    const uploadClaim = async () => {
        if (!files || !files.length) return;

        const localProcessingResults: ProcessedClaimStatus[] | ProcessedPdfClaimStatus[] = processResults;

        setIsPageBusy(true);
        await files.reduce(async (promise, file) => {
            await promise;
            const uploadClaimResult = await requestClaimUploadUrl({
                resellerId,
                filename: sanitizeFilenameISO88591(file.name),
                mimeType: file.type,
            });

            // upload file
            try {
                setFileProgress(file.name, 0);
                Logger.debug(`uploading File: ${uploadClaimResult.url}`);
                await PutClient.put(uploadClaimResult.url, file);
                Logger.debug(`File Uploaded Successfully`);
                setFileProgress(file.name, 33);
                // between uploading the file and processing it. HDSS needs time to move the file
                await delay(3000);
                setFileProgress(file.name, 66);
                const results = await submitClaim(
                    uploadClaimResult.id,
                    files.findIndex((match) => match.name === file.name),
                    file.type
                );
                localProcessingResults.push({ ...results[0], fileName: file.name });
                setProcessResults(localProcessingResults);
                setFileProgress(file.name, 100);
            } catch (error) {
                Logger.debug(`failed to upload file with error: ${error}`);
                showError(t('uploadClaim-errorMessage'));
            }
        }, Promise.resolve());
        setProgress([]);
        setFiles([]);
        setIsPageBusy(false);
        Logger.info('statusFormat', statusFormat);
        Logger.info('processed results', localProcessingResults);
    };

    const submitClaim = async (
        uploadedClaimFileId: string,
        fileIndex: number,
        fileType: string
    ): Promise<ProcessedClaimStatus[] | ProcessedPdfClaimStatus[]> => {
        const processClaimResult = await submitClaimRetry(uploadedClaimFileId, fileIndex, fileType, 10);

        // Give some time for SCS to import and process the claim
        if ('resellerClaimNumber' in processClaimResult.claimUploadedRequests[0]) {
            await delay(SCS_IMPORT_WAIT_TIME_MS);
        }

        return processClaimResult.claimUploadedRequests;
    };

    const delayPeriod = 250;
    const submitClaimRetry = async (
        uploadedClaimFileId: string,
        fileIndex: number,
        fileType: string,
        retryCount: number,
        delayMultiplier = 1
    ): Promise<ProcessClaimUploadOutput | ProcessPdfClaimUploadOutput> => {
        try {
            Logger.info('Receipt Date:', receiptDates[fileIndex]);
            switch (fileType) {
                case 'application/pdf':
                    return await processPdfClaimUpload({
                        resellerId,
                        uploadId: uploadedClaimFileId,
                        claimType: claimTypes[fileIndex]!,
                        receiptDate: new Date(receiptDates[fileIndex]).getTime() / 1000,
                        receiptMethod: receiptMethods[fileIndex],
                    });
                default:
                    return await processClaimUpload({
                        resellerId,
                        uploadId: uploadedClaimFileId,
                        isOverride: isOverride,
                        receiptDate: new Date(receiptDates[fileIndex]).getTime() / 1000,
                        receiptMethod: receiptMethods[fileIndex],
                    });
            }
        } catch (e: any) {
            if (e.errorCode !== 'UploadNotAvailable' || retryCount === 0) {
                Logger.debug(`error code ${e} not retryable throwing`);
                return Promise.reject(e);
            }
            await delay(delayPeriod * delayMultiplier);
            return submitClaimRetry(uploadedClaimFileId, retryCount - 1, fileType, delayMultiplier + 1);
        }
    };

    const showGeneratePreFilledPromotionalTemplateModal = () => {
        modal.prompt({
            title: t('generatePromotionalClaimPopupTitle'),
            content: <GeneratePreFilledClaimTemplatePopup resellerId={resellerId} />,
            options: {
                enableCornerCloseButton: false,
            },
        });
    };

    const statusFormat: ColumnFormat = useMemo(() => {
        const getText = (status: string) => t(`processedClaimStatus_${status}`, { context: status });

        return {
            render: getText,
        };
    }, [t]);

    const messageFormat: ColumnFormat = useMemo(() => {
        const getText = (status: string, row: any) => {
            if (status === null) return t.cognate(' ');
            if (status === 'UNSUCCESSFUL' && row.processingErrors?.length > 0) {
                return t('processedClaimMessage_UNSUCCESSFUL_VALIDATION', {
                    count: row.processingErrors.length,
                });
            }
            return t(`processedClaimMessage_${status}`, { context: status });
        };

        return {
            render: getText,
        };
    }, [t]);

    const errorFormat: ColumnFormat = useMemo(() => {
        const getErrors = (errors: ClaimProcessingError[]) => {
            if (!errors || errors.length === 0) return <></>;

            return (
                <HelpPopover header={errorT('error-popoverTitle')} label={errorT('error-seeErrors')}>
                    <Text tag={'ul'}>
                        {errors.map((error) => (
                            <li key={error.errorMessage}>
                                {errorT(`uploadError-${error.errorCode}`, error.parameters)}
                            </li>
                        ))}
                    </Text>
                </HelpPopover>
            );
        };

        return {
            render: getErrors,
        };
    }, [errorT]);

    const claimLinkFormat: ColumnFormat = useMemo(() => {
        const getLink = (resellerClaimNumber: any) => {
            if (resellerClaimNumber) {
                return (
                    // used anchor as Meridian Link is not working to open the link in a new tab
                    <a
                        href={`/claims/view/claims/claim/${resellerClaimNumber}`}
                        target={'_blank'}
                        rel={'noopener noreferrer'}
                        style={{ color: '#0047ab', textDecoration: 'none' }}
                    >
                        {resellerClaimNumber}
                    </a>
                );
            }
            return <></>;
        };

        return {
            render: getLink,
        };
    }, []);

    const columns: SuperPaginatedTableColumn[] = [
        {
            sourceProperty: 'fileName',
            title: t('uploadedClaim-columnHeader'),
            format: textFormat,
            isKey: true,
        },
        {
            sourceProperty: 'status',
            title: t('uploadedClaimStatus-columnHeader'),
            format: statusFormat,
        },
        {
            sourceProperty: 'processingErrors',
            title: t('uploadedClaimErrors-columnHeader'),
            format: errorFormat,
        },
        {
            sourceProperty: 'status',
            title: t('uploadedClaimMessage-columnHeader'),
            format: messageFormat,
        },
        {
            sourceProperty: 'resellerClaimNumber',
            title: t('viewClaim-columnHeader'),
            format: claimLinkFormat,
        },
    ];

    const showTemplateSelectionPopup = () => {
        modal.prompt({
            title: t('uploadTemplates-documentsPopupTitle'),
            content: () => <TemplateSelectionPopup resellerId={resellerId} countryCode={countryCode} />,
        });
    };

    return (
        <Column>
            <Row>
                <Heading level={4}>
                    <Text>{[`Upload Claims`]}</Text>
                </Heading>
            </Row>
            {isPdfClaimEnabled ? (
                <>
                    <Text>{t('uploadClaim-instructionPdfEnabled-1')}</Text>
                    <Text>{t('uploadClaim-instructionPdfEnabled-2')}</Text>
                    <Text>{t('uploadClaim-instructionPdfEnabled-3')}</Text>
                </>
            ) : (
                <Text>{t('uploadClaim-instruction')}</Text>
            )}
            <Row>
                <Link onClick={() => showTemplateSelectionPopup()}>{t('downloadTemplates-buttonLabel')}</Link>
                {isPreFilledClaimGeneratorEnabled && (
                    <Link onClick={() => showGeneratePreFilledPromotionalTemplateModal()}>
                        {t('generatePromotionalClaim-buttonLabel')}
                    </Link>
                )}
            </Row>
            <Row>
                <Tile title={t('uploadClaim-tileTitle')} width={'100%'}>
                    <Column>
                        <FileInput
                            type={'multiple'}
                            uploadButtonLabel={t('chooseFile-buttonLabel')}
                            uploadButtonDisabled={isPageBusy}
                            accept={isPdfClaimEnabled ? '.xlsx, .pdf' : '.xlsx'}
                            onFileAttached={(acceptedFiles) => {
                                Logger.debug(acceptedFiles);
                                setOrUpdateFiles(acceptedFiles);
                            }}
                        />
                        <Text>{t('chooseFile-buttonHint')}</Text>
                        {files && files.length ? (
                            <>
                                <Table headerRows={1}>
                                    <TableRow>
                                        <TableCell>{t('uploadClaim-file')}</TableCell>
                                        {isOverride && (
                                            <>
                                                <TableCell />
                                                <TableCell />
                                            </>
                                        )}
                                        {filesListContainsPdf && <TableCell />}
                                        <TableCell>{t('uploadClaim-actions')}</TableCell>
                                    </TableRow>
                                    {files.map((file, index) => (
                                        <TableRow key={`${file.name}tablerow`}>
                                            <TableCell>
                                                <FileDetails
                                                    file={(file as unknown) as FileDetailsFile}
                                                    uploadPercentage={
                                                        progress.find((f) => f.fileName === file.name)?.progress || 0
                                                    }
                                                    key={((file as unknown) as FileDetailsFile).name}
                                                    uploadComplete={
                                                        progress.find((f) => f.fileName === file.name)?.progress === 100
                                                    }
                                                />
                                            </TableCell>
                                            {isOverride && (
                                                <>
                                                    <TableCell>
                                                        <Input
                                                            type={'date'}
                                                            onChange={(date) => updateReceiptDate(date, index)}
                                                            label={t('uploadClaim-receiptDate')}
                                                            value={
                                                                receiptDates &&
                                                                receiptDates.length &&
                                                                receiptDates[index] !== undefined
                                                                    ? receiptDates[index].toString()
                                                                    : ''
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Select
                                                            onChange={(method) => updateReceiptMethod(method, index)}
                                                            label={t('uploadClaim-receiptMethod')}
                                                            value={
                                                                receiptMethods &&
                                                                receiptMethods.length &&
                                                                receiptMethods[index] !== undefined
                                                                    ? receiptMethods[index]
                                                                    : ''
                                                            }
                                                        >
                                                            {Object.entries(ClaimReceiptMethods).map((k) => {
                                                                return (
                                                                    <SelectOption
                                                                        label={k[1]}
                                                                        key={k[0]}
                                                                        value={k[1]}
                                                                    />
                                                                );
                                                            })}
                                                        </Select>
                                                    </TableCell>
                                                </>
                                            )}
                                            {file.type === 'application/pdf' && (
                                                <TableCell>
                                                    <Select
                                                        onChange={(claimType) => updateClaimType(claimType, index)}
                                                        label={t('uploadClaim-claimType')}
                                                        value={claimTypes[index]}
                                                        disabled={!areNonTprClaimTypesEnabledForPdfClaims}
                                                    >
                                                        {Object.entries(ClaimType).map((k) => {
                                                            return (
                                                                <SelectOption key={k[0]} label={k[1]} value={k[1]} />
                                                            );
                                                        })}
                                                    </Select>
                                                </TableCell>
                                            )}
                                            {file.type !== 'application/pdf' && filesListContainsPdf && <TableCell />}
                                            <TableCell>
                                                <Button type={'secondary'} onClick={() => deleteFile(index)}>
                                                    {t('delete-buttonLabel')}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </Table>
                            </>
                        ) : undefined}
                    </Column>
                </Tile>
            </Row>
            <Row>
                <Button type={'secondary'} onClick={cancel}>
                    {t('cancel-buttonLabel')}
                </Button>
                <Button
                    type={'primary'}
                    isBusy={isPageBusy}
                    disabled={
                        !files.length ||
                        (isOverride &&
                            (receiptMethods.some((obj) => obj === undefined) ||
                                receiptDates.some((obj) => obj === undefined))) ||
                        (filesListContainsPdf &&
                            claimTypes.some(
                                (claimType, index) =>
                                    claimType === undefined && files[index]?.type === 'application/pdf'
                            ))
                    }
                    onClick={() => uploadClaim()}
                >
                    {t('submit-buttonLabel')}
                </Button>
            </Row>
            {processResults.length ? (
                <Row>
                    <Tile
                        width={'100%'}
                        title={t('processUploadedClaimsResults-tableHeading')}
                        contentSpacingInset={'none'}
                    >
                        <SuperPaginatedTable
                            data={processResults}
                            layout={{
                                pageSize: 10,
                                searchPlaceholder: t('findResources-searchBoxPlaceholder'),
                                columns,
                            }}
                        />
                    </Tile>
                </Row>
            ) : undefined}
        </Column>
    );
};

const mapStateToProps = ({ resellerReducer, userReducer }: RootState) => {
    return {
        user: userReducer.user,
        resellerId: resellerReducer.selectedReseller.value!.resellerId,
        countryCode: resellerReducer.selectedReseller.value!.countryCode,
    };
};

const connector = connect(mapStateToProps);
export type UploadClaimPageProps = ConnectedProps<typeof connector>;
export default connector(UploadClaimPage);
